/**
 * ลำดับขีด — วาดทีละเส้นตามลำดับที่คนญี่ปุ่นเขียนจริง
 *
 * ทำไมเป็นอนิเมชันไม่ใช่ภาพนิ่งพร้อมเลขกำกับ: ภาพนิ่งบอกว่าขีดไหนก่อน แต่ไม่บอก
 * ทิศทาง ซึ่งเป็นครึ่งหนึ่งของเรื่อง (へ ลากซ้ายไปขวา · ノ ลากลงซ้าย) และเลข 14 ตัว
 * บนตัวเดียวอ่านไม่ทันอยู่ดี · เลขจึงเป็นตัวเสริมที่เปิดเองได้ ไม่ใช่ตัวหลัก
 *
 * เทคนิค: วาดเงาจาง ๆ ไว้ทั้งตัวก่อน แล้ว "เขียนทับ" ด้วย stroke-dashoffset ที่วิ่ง
 * จากความยาวเส้นไป 0 — ได้เส้นที่โผล่จากหัวไปหางตามทิศที่ KanjiVG เก็บไว้
 *
 * ภาพวาดทับในช่องโจทย์เดิม ไม่ใช่กล่องใหม่ — ตัวอักษรควรอยู่ที่เดิมที่ตาจ้องอยู่แล้ว
 */
import { STROKE_BOX, strokesForCard, type StrokeChar } from '@/core/strokes'
import type { Card } from '@/core/types'
import { saveSettings, settings } from './settings'

/** ms ต่อขีดที่ความเร็วปกติ — ช้าพอให้ตาตามทัน เร็วพอไม่น่ารำคาญ */
const BASE_MS = 620
const GAP_MS = 90

const SPEEDS: Array<[string, number]> = [
  ['ช้า', 1.7],
  ['ปกติ', 1],
  ['เร็ว', 0.55],
]

export interface StrokePlayer {
  /** ภาพวาด — วางทับในช่องโจทย์ */
  overlay: HTMLElement
  /** แถบควบคุม — วางใต้ช่อง */
  bar: HTMLElement
  visible: boolean
  /**
   * จองที่ให้แถบควบคุมไว้ล่วงหน้า
   *
   * ถ้าปล่อยให้แถบโผล่ตอนกดดู เนื้อหาใต้ช่องจะเลื่อนลง 59px — มือที่กำลังจะกด
   * ตัวเลือกอยู่ก็กดพลาด · โหมดไหนที่กดดูได้ก็กันที่ไว้ตั้งแต่แรกเลย
   */
  arm(on: boolean): void
  /** โหลดข้อมูลของการ์ดใบนี้ · false = ไม่มีลำดับขีด ปุ่มควรซ่อน */
  load(card: Card): Promise<boolean>
  show(): void
  hide(): void
  play(): void
  /** เน้นขีดของชิ้นส่วนนี้ · null = เลิกเน้น · คืนค่าว่าเน้นได้จริงไหม */
  highlight(part: string | null): boolean
  /** ชิ้นส่วนนี้ตรงกับที่ KanjiVG แบ่งไว้ไหม */
  canFocus(part: string): boolean
}

interface Glyph {
  svg: SVGSVGElement
  live: SVGPathElement[]
  lens: number[]
}

const NS = 'http://www.w3.org/2000/svg'

function svgEl<K extends keyof SVGElementTagNameMap>(tag: K): SVGElementTagNameMap[K] {
  return document.createElementNS(NS, tag)
}

export function createStrokePlayer(): StrokePlayer {
  const overlay = document.createElement('div')
  overlay.className = 'strokes'
  overlay.hidden = true

  const bar = document.createElement('div')
  bar.className = 'stroke-bar w'
  bar.hidden = true

  const replay = document.createElement('button')
  replay.type = 'button'
  replay.className = 'stroke-btn'
  replay.textContent = '↻ เล่นซ้ำ'

  const speeds = document.createElement('div')
  speeds.className = 'stroke-speeds'

  const numToggle = document.createElement('button')
  numToggle.type = 'button'
  numToggle.className = 'stroke-btn'

  bar.append(replay, speeds, numToggle)

  let glyphs: Glyph[] = []
  let parts: StrokeChar['p']
  let timers: number[] = []
  let focused = -1
  /** กันใบที่โหลดช้ามาวาดทับใบที่อยู่บนจอแล้ว */
  let loadSeq = 0

  const clearTimers = (): void => {
    for (const t of timers) clearTimeout(t)
    timers = []
  }

  const applyNumbers = (): void => {
    overlay.classList.toggle('show-nums', settings.strokeNumbers)
    numToggle.textContent = settings.strokeNumbers ? 'ซ่อนเลข' : '① เลขกำกับ'
    numToggle.setAttribute('aria-pressed', String(settings.strokeNumbers))
  }

  const renderSpeeds = (): void => {
    speeds.replaceChildren()
    for (const [label, mult] of SPEEDS) {
      const b = document.createElement('button')
      b.type = 'button'
      b.className = 'stroke-chip'
      b.textContent = label
      b.setAttribute('aria-pressed', String(settings.strokeSpeed === mult))
      b.addEventListener('click', () => {
        settings.strokeSpeed = mult
        saveSettings()
        renderSpeeds()
        play()
      })
      speeds.append(b)
    }
  }

  const play = (): void => {
    clearTimers()
    focused = -1
    const step = BASE_MS * settings.strokeSpeed
    let at = 0
    // นับต่อเนื่องข้ามกรอบ เพื่อให้ ちゃ เขียน ち จบก่อนแล้วค่อยขึ้น ゃ
    for (const g of glyphs) {
      g.svg.classList.remove('is-focus')
      g.live.forEach((p, i) => {
        p.classList.remove('is-part', 'is-dim')
        p.style.transition = 'none'
        p.style.strokeDashoffset = String(g.lens[i])
        timers.push(
          window.setTimeout(() => {
            p.style.transition = `stroke-dashoffset ${step}ms linear`
            p.style.strokeDashoffset = '0'
          }, at + i * (step + GAP_MS)),
        )
      })
      at += g.live.length * (step + GAP_MS)
    }
  }

  /** ข้ามอนิเมชัน แสดงเต็มตัวทันที */
  const settle = (): void => {
    clearTimers()
    for (const g of glyphs) {
      for (const p of g.live) {
        p.style.transition = 'none'
        p.style.strokeDashoffset = '0'
      }
    }
  }

  const build = (data: StrokeChar[]): void => {
    overlay.replaceChildren()
    glyphs = []
    focused = -1
    // ชิ้นส่วนกดได้เฉพาะตัวเดี่ยว — yōon ไม่มีชิ้นส่วนอยู่แล้ว
    parts = data.length === 1 ? data[0]?.p : undefined

    for (const s of data) {
      const svg = svgEl('svg')
      svg.setAttribute('viewBox', `0 0 ${STROKE_BOX} ${STROKE_BOX}`)
      svg.setAttribute('class', 'stroke-svg')
      svg.setAttribute('aria-hidden', 'true')

      for (const d of s.d) {
        const ghost = svgEl('path')
        ghost.setAttribute('class', 'stroke-ghost')
        ghost.setAttribute('d', d)
        svg.append(ghost)
      }

      const live: SVGPathElement[] = []
      const lens: number[] = []
      s.d.forEach((d, i) => {
        const p = svgEl('path')
        p.setAttribute('class', 'stroke-live')
        p.setAttribute('d', d)
        svg.append(p)
        const len = Math.ceil(p.getTotalLength())
        p.style.strokeDasharray = String(len)
        p.style.strokeDashoffset = '0'
        live.push(p)
        lens.push(len)

        // เลขกำกับวางที่หัวขีด — จุดที่ปากกาแตะกระดาษ
        const start = p.getPointAtLength(0)
        const t = svgEl('text')
        t.setAttribute('class', 'stroke-num')
        t.setAttribute('x', String(start.x))
        t.setAttribute('y', String(start.y))
        t.textContent = String(i + 1)
        svg.append(t)
      })

      overlay.append(svg)
      glyphs.push({ svg, live, lens })
    }
  }

  const highlight = (part: string | null): boolean => {
    const g = glyphs[0]
    if (!g || !parts) return false
    const i = part === null ? -1 : parts.findIndex(([ch]) => ch === part)
    if (part !== null && i < 0) return false

    focused = i === focused ? -1 : i
    const range = focused >= 0 ? parts[focused] : undefined
    clearTimers()
    g.svg.classList.toggle('is-focus', !!range)
    g.live.forEach((p, n) => {
      p.style.transition = 'none'
      p.style.strokeDashoffset = '0'
      const inPart = !!range && n >= range[1] && n < range[1] + range[2]
      p.classList.toggle('is-part', inPart)
      p.classList.toggle('is-dim', !!range && !inPart)
    })
    return focused >= 0
  }

  replay.addEventListener('click', () => play())
  numToggle.addEventListener('click', () => {
    settings.strokeNumbers = !settings.strokeNumbers
    saveSettings()
    applyNumbers()
  })

  const api: StrokePlayer = {
    overlay,
    bar,
    visible: false,
    async load(card) {
      clearTimers()
      // ชุดคันจิที่ยังไม่โหลดใช้เวลาเป็นวินาที ระหว่างนั้นกดข้ามไปใบอื่นได้
      // ใบที่มาช้าต้องไม่วาดทับใบที่อยู่บนจอแล้ว
      const seq = ++loadSeq
      const data = await strokesForCard(card)
      if (seq !== loadSeq) return false
      if (!data) return false
      build(data)
      applyNumbers()
      renderSpeeds()
      return true
    },
    arm(on) {
      bar.hidden = !on
    },
    show() {
      api.visible = true
      overlay.hidden = false
      bar.hidden = false
      bar.classList.remove('is-idle')
      // ปิดอนิเมชันไว้ = อยากเห็นทั้งตัวทันที เลขกำกับจึงต้องโผล่มาแทน
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
        overlay.classList.add('show-nums')
        settle()
      } else {
        play()
      }
    },
    hide() {
      api.visible = false
      overlay.hidden = true
      // ไม่ใช่ hidden — ที่ยังถูกจองไว้ เนื้อหาข้างล่างจะได้ไม่ขยับ
      bar.classList.add('is-idle')
      clearTimers()
    },
    play,
    highlight,
    canFocus: (part) => !!parts?.some(([ch]) => ch === part),
  }
  return api
}
